import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { Box, Typography, Button, CircularProgress } from "@mui/material";

import { colors } from "../../../../theme/palette";

import { Post } from "../../types/post.type";
import { PostTypeEnum } from "../../enums/post.enum";
import { FeedItem } from "../../api/types/feed.types";
import { useFeedListQuery } from "../../api/queries/useFeedListQuery";

import PostCard from "../Posts";
import FullScreenPostModal from "../FullScreenPostModal";
import { useFullScreenPost } from "../FullScreenPostModal/useFullScreenPost";

const FeedList = () => {
  const { t } = useTranslation("feed", { keyPrefix: "feedList" });
  const [searchParams] = useSearchParams();
  const postType = searchParams.get("type") as PostTypeEnum;

  const { isFullScreenPostOpened, fullScreenPost, openFullScreenPost, closeFullScreenPost } = useFullScreenPost();
  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, error, refetch } =
    useFeedListQuery(postType);
  const posts = data?.posts;

  // Find the updated post from the feed list whenever it changes
  const updatedFullScreenPost = useMemo(() => {
    if (!fullScreenPost || !posts) return fullScreenPost;
    const foundPost = posts.find((item: FeedItem) => item.post.id === fullScreenPost.id);
    return foundPost ? foundPost.post : fullScreenPost;
  }, [posts, fullScreenPost]);

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
        <CircularProgress size={40} sx={{ color: colors.Green100, mb: 2 }} />
        <Typography variant="body14Regular" color={colors.Dark600}>
          {t("loadingPosts")}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4} px={2}>
        <Typography variant="body16Bold" color={colors.Red100} mb={1}>
          {t("errorFetchingPosts")}
        </Typography>
        <Button variant="primary-md" onClick={() => refetch()}>
          {t("tryAgain")}
        </Button>
      </Box>
    );
  }

  if (!posts && !isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Typography variant="body14Regular" color={colors.Dark600}>
          {t("noPostsFound")}
        </Typography>
      </Box>
    );
  }

  if (!isLoading && !error && posts && posts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Typography variant="body14Regular" color={colors.Dark600}>
          {t("noPostsFound")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {posts &&
        posts.map((item: FeedItem, index: number) => (
          <div key={item.post.id} ref={index === posts.length - 3 ? ref : null}>
            <PostCard
              post={item.post}
              onClickOnMedia={openFullScreenPost}
              isRepostedPost={item.postType === PostTypeEnum.REPOST}
              reposterUser={item.additionalProperties?.user}
            />
          </div>
        ))}
      {updatedFullScreenPost && (
        <FullScreenPostModal
          key={updatedFullScreenPost.id}
          open={isFullScreenPostOpened}
          closeModal={closeFullScreenPost}
          post={updatedFullScreenPost as Post}
        />
      )}
      {isFetchingNextPage && (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={2}>
          <CircularProgress size={30} sx={{ color: colors.Green100, mb: 1 }} />
          <Typography variant="body14Regular" color={colors.Dark600}>
            {t("loadingMore")}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default FeedList;
